import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { SplitAuthLayout } from "../components/SplitAuthLayout";
import { useAuth } from "../lib/auth";

export function MagicLink() {
  const { verifyToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "error">("verifying");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("missing_token");
      return;
    }
    // Scrub the token from browser history + server access logs immediately.
    // The token is still valid for the in-flight verify call (we captured it
    // above) but is no longer visible in window.location after this point.
    window.history.replaceState({}, "", "/auth");

    verifyToken(token)
      .then(() => navigate("/engagements", { replace: true }))
      .catch((err) => {
        setStatus("error");
        setErrorMsg(err instanceof Error ? err.message : "verification_failed");
      });
  }, [searchParams, verifyToken, navigate]);

  if (status === "verifying") {
    return (
      <SplitAuthLayout>
        <div style={styles.step}>STEP 2 / 2</div>
        <h3 style={styles.heading}>Verifying sign-in link…</h3>
        <p style={styles.sub}>
          Exchanging your one-time token for an operator session. This should
          take less than a second.
        </p>
        <p style={styles.helpLine}>
          <span style={styles.dot} aria-hidden="true" />
          contacting api · ip allowlist active
        </p>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <div style={styles.step}>STEP 2 / 2</div>
      <h3 style={{ ...styles.heading, color: "var(--error)" }}>
        Sign-in failed
      </h3>
      <p style={styles.sub}>{errorMsg}</p>
      <p style={styles.foot}>
        Request a new link from the{" "}
        <a href="/login" style={styles.link}>
          sign-in page
        </a>
        .
      </p>
    </SplitAuthLayout>
  );
}

const styles = {
  step: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--text-muted)",
    letterSpacing: 1.4,
    marginBottom: 8,
  },
  heading: {
    margin: "0 0 8px",
    fontSize: 19,
    color: "var(--text-primary)",
  },
  sub: {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: "0 0 18px",
    lineHeight: 1.55,
  },
  helpLine: {
    marginTop: 14,
    fontSize: 10.5,
    color: "var(--text-muted)",
    fontFamily: "var(--font-mono)",
    display: "flex",
    alignItems: "center",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "var(--success)",
    display: "inline-block",
    marginRight: 6,
    boxShadow: "0 0 6px rgba(34,197,94,0.5)",
  },
  foot: {
    fontSize: 11,
    color: "var(--text-muted)",
    margin: 0,
  },
  link: {
    color: "var(--accent-bright)",
    textDecoration: "none",
    fontWeight: 500,
  },
};
